import React, { useEffect, useState, useRef } from 'react';
import { Filter, ArrowUpDown, ArrowUp, ArrowDown, Search, X, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { MOCK_LEADS, Lead, USERS, AVAILABLE_CAMPAIGNS } from '../data/mockLeads';
import { LeadRow } from './LeadRow';
import { Button } from './Button';
import { LeadActionModal, ActionMode } from './LeadActionModal';
import { useRole } from '../contexts/RoleContext';
type PurposeFilter = 'Purchase' | 'Refinance';
type SortKey = 'loanAmount' | 'lastContact';
type SortDir = 'asc' | 'desc';
const PAGE_SIZE = 20;
const PURPOSE_OPTIONS: {
  value: PurposeFilter;
  label: string;
}[] = [{
  value: 'Purchase',
  label: 'Purchase'
}, {
  value: 'Refinance',
  label: 'Refinance'
}];
// Toggle a value in a filter array (add if absent, remove if present).
function toggleValue<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
}
export function LeadQueue({
  ownerId
}: {
  ownerId?: string;
}) {
  const {
    role
  } = useRole();
  // Loan officers and sales managers can enroll borrowers into a campaign.
  const canAssignCampaign = role === 'officer' || role === 'manager';
  const [leads, setLeads] = useState(MOCK_LEADS);
  // 'all' and 'company' are aggregate scopes that span multiple owners.
  const isAggregate = ownerId === 'all' || ownerId === 'company';
  const showOwner = isAggregate;
  const [filter, setFilter] = useState<'all' | 'pending' | 'contacted'>('all');
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [purposes, setPurposes] = useState<PurposeFilter[]>([]);
  const [managerFilters, setManagerFilters] = useState<string[]>([]);
  const [officerFilters, setOfficerFilters] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);
  const filterRef = useRef<HTMLDivElement>(null);
  // Org helpers for the manager / loan-officer filters (aggregate views only).
  const managers = USERS.filter(u => u.role === 'manager');
  const officers = USERS.filter(u => u.role === 'officer');
  const officerOptions = managerFilters.length === 0 ? officers : officers.filter(o => o.managerId && managerFilters.includes(o.managerId));
  // Resolve which manager "owns" a lead: either the owner is a manager, or the
  // owner is an officer reporting to that manager.
  const managerOfLead = (lead: Lead) => {
    const owner = USERS.find(u => u.id === lead.ownerId);
    if (!owner) return undefined;
    return owner.role === 'manager' ? owner.id : owner.managerId;
  };
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const [activeModal, setActiveModal] = useState<{
    lead: Lead | null;
    mode: ActionMode;
  }>({
    lead: null,
    mode: 'call'
  });
  const activeFilterCount = purposes.length + (isAggregate ? managerFilters.length : 0) + (isAggregate ? officerFilters.length : 0);
  const clearFilters = () => {
    setPurposes([]);
    setManagerFilters([]);
    setOfficerFilters([]);
  };
  // Close the filter dropdown when clicking outside it
  useEffect(() => {
    if (!filtersOpen) return;
    const onClick = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFiltersOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [filtersOpen]);
  const filteredLeads = leads.filter(lead => {
    if (ownerId && !isAggregate && lead.ownerId !== ownerId) return false;
    if (filter !== 'all' && lead.status !== filter) return false;
    if (purposes.length > 0 && !purposes.includes(lead.loanPurpose as PurposeFilter)) return false;
    if (isAggregate && managerFilters.length > 0) {
      const m = managerOfLead(lead);
      if (!m || !managerFilters.includes(m)) return false;
    }
    if (isAggregate && officerFilters.length > 0 && !officerFilters.includes(lead.ownerId)) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return lead.name.toLowerCase().includes(q) || lead.email.toLowerCase().includes(q) || lead.campaignName.toLowerCase().includes(q);
  });
  const sortedLeads = sortKey ? [...filteredLeads].sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'loanAmount') {
      cmp = a.loanAmount - b.loanAmount;
    } else {
      cmp = new Date(a.lastContact).getTime() - new Date(b.lastContact).getTime();
    }
    return sortDir === 'asc' ? cmp : -cmp;
  }) : filteredLeads;
  // Pagination (20 per page by default).
  const totalRecords = sortedLeads.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pagedLeads = sortedLeads.slice(startIndex, startIndex + PAGE_SIZE);
  // Reset to the first page whenever the result set changes.
  useEffect(() => {
    setPage(1);
  }, [filter, search, purposes, managerFilters, officerFilters, ownerId]);
  // Keep the officer selection consistent with the selected managers: drop any
  // selected officers that no longer report to a selected manager.
  useEffect(() => {
    if (managerFilters.length === 0) return;
    setOfficerFilters(prev => prev.filter(id => officerOptions.some(o => o.id === id)));
  }, [managerFilters]);
  const SortIcon = ({
    column
  }: {
    column: SortKey;
  }) => {
    if (sortKey !== column) return <ArrowUpDown size={14} className="text-slate-400" data-id="element-87" data-mp="2bn0lsvob7zx5" />;
    return sortDir === 'asc' ? <ArrowUp size={14} className="text-blue-600" data-id="element-88" data-mp="2bpddi0jvdone" /> : <ArrowDown size={14} className="text-blue-600" data-id="element-89" data-mp="13owi5mwgtpxq" />;
  };
  const markContacted = (id: string) => setLeads(prev => prev.map(l => l.id === id ? {
    ...l,
    status: 'contacted',
    lastContact: new Date().toISOString()
  } : l));
  const handleAction = (id: string, action: string) => {
    if (action === 'call' || action === 'email' || action === 'schedule') {
      const lead = leads.find(l => l.id === id) ?? null;
      setActiveModal({
        lead,
        mode: action as ActionMode
      });
    }
  };
  // Enroll a borrower who is not yet in a campaign into the chosen email campaign.
  const assignCampaign = (id: string, campaignName: string) => setLeads(prev => prev.map(l => l.id === id ? {
    ...l,
    campaignName,
    campaignType: 'email'
  } : l));
  const setNextFollowUp = (id: string, nextFollowUp: string) => setLeads(prev => prev.map(l => l.id === id ? {
    ...l,
    nextFollowUp
  } : l));
  const handleModalSubmit = (leadId: string, mode: ActionMode, payload: Record<string, string>) => {
    // Only logging a call counts as contacting the lead.
    if (mode === 'call') {
      markContacted(leadId);
    } else if (mode === 'schedule' && payload.nextFollowUp) {
      setNextFollowUp(leadId, payload.nextFollowUp);
    }
    setActiveModal({
      lead: null,
      mode: 'call'
    });
  };
  return <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-id="element-90" data-mp="f4jde16zemux">
      <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-white" data-id="element-91" data-mp="p0w72z2j10po">
        <div data-id="element-92" data-mp="k1rzgcv93b1g">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-93" data-mp="k2bgzsgznxey">
            Today's Follow-ups
          </h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-94" data-mp="30vmso6inyo8s">
            Leads that require your attention today.
          </p>
        </div>

        <div className="flex items-center gap-3" data-id="element-95" data-mp="35sd8csqk3hto">
          <div className="flex bg-slate-100 p-1 rounded-lg" data-id="element-96" data-mp="3atarb7je4fw8">
            <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => setFilter('all')} data-id="element-97" data-mp="3aqy8ks5t61i4">
              All
            </button>
            <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === 'pending' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => setFilter('pending')} data-id="element-98" data-mp="2n13xog51un0i">
              Pending
            </button>
            <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === 'contacted' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => setFilter('contacted')} data-id="element-99" data-mp="24cp7tw6gxf1a">
              Completed
            </button>
          </div>
          <div className="relative" data-id="element-100" data-mp="cfuz2xlqmkc5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} data-id="element-101" data-mp="cizaog1j8icu" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search leads..." aria-label="Search follow-ups" className="w-48 pl-9 pr-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" data-id="element-102" data-mp="2j8fec55dzbp" />
          </div>
          <div className="relative" ref={filterRef} data-id="element-103" data-mp="3fwjxtxf2wpr9">
            <Button variant={filtersOpen || activeFilterCount > 0 ? 'secondary' : 'outline'} size="sm" icon={<Filter size={16} data-id="element-105" data-mp="116ebetl2qunm" />} onClick={() => setFiltersOpen(o => !o)} aria-expanded={filtersOpen} aria-haspopup="true" data-id="element-104" data-mp="3az1wot7azett">
              Filters
              {activeFilterCount > 0 && <span className="ml-2 inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1 rounded-full bg-blue-600 text-white text-xs font-semibold" data-id="element-106" data-mp="15yz1bk0v0nlv">
                  {activeFilterCount}
                </span>}
            </Button>

            {filtersOpen && <div role="menu" className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-20 p-4 space-y-4" data-id="element-107" data-mp="raui8exwaa2k">
                <div className="flex items-center justify-between" data-id="element-108" data-mp="22p845e938ffy">
                  <h3 className="text-sm font-semibold text-slate-900" data-id="element-109" data-mp="22rkpm1e217xr">
                    Filters
                  </h3>
                  {activeFilterCount > 0 && <button type="button" onClick={clearFilters} className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-110" data-mp="265uealilfqxs">
                      <X size={12} data-id="element-111" data-mp="218b2ds9dbq90" /> Clear
                    </button>}
                </div>

                {isAggregate && <>
                    <div data-id="element-112" data-mp="2oyot9fg7vj20">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-113" data-mp="2tx03vsec5xla">
                        Sales Manager
                      </p>
                      <div className="space-y-1" data-id="element-114" data-mp="2f7txnkh2z73w">
                        {managers.map(m => <CheckOption key={m.id} label={m.name} checked={managerFilters.includes(m.id)} onChange={() => setManagerFilters(prev => toggleValue(prev, m.id))} data-id="element-115" data-mp="2k5cahly7y6h6" />)}
                      </div>
                    </div>

                    <div data-id="element-116" data-mp="3cn4zmpgqw93u">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-117" data-mp="38k898ckyr8pc">
                        Loan Officer
                      </p>
                      <div className="space-y-1 max-h-40 overflow-y-auto" data-id="element-118" data-mp="33mf5rrb1ossr">
                        {officerOptions.map(o => <CheckOption key={o.id} label={o.name} checked={officerFilters.includes(o.id)} onChange={() => setOfficerFilters(prev => toggleValue(prev, o.id))} data-id="element-119" data-mp="2tu9dshksmbdk" />)}
                      </div>
                    </div>
                  </>}

                <div data-id="element-120" data-mp="10a8wl0awuo54">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-121" data-mp="14dnlyhkqogv8">
                    Loan Purpose
                  </p>
                  <div className="space-y-1" data-id="element-122" data-mp="19bgon0mu2sqr">
                    {PURPOSE_OPTIONS.map(opt => <CheckOption key={opt.value} label={opt.label} checked={purposes.includes(opt.value)} onChange={() => setPurposes(prev => toggleValue(prev, opt.value))} data-id="element-123" data-mp="co2d9hw1dlzl" />)}
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-124" data-mp="3u1xsg5mmpe0r">
        <table className="w-full text-left border-collapse" data-id="element-125" data-mp="3u0mlxy1sts5y">
          <thead data-id="element-126" data-mp="20l1rwl7eeezv">
            <tr className="bg-slate-50 border-b border-slate-200" data-id="element-127" data-mp="20kiad5lntsmd">
              <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-128" data-mp="1vm8e0ro78lz6">
                Lead Details
              </th>
              {showOwner && <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-129" data-mp="24lbwbtfjkxcm">
                  Owner
                </th>}
              <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider" aria-sort={sortKey === 'loanAmount' ? sortDir === 'asc' ? 'ascending' : 'descending' : 'none'} data-id="element-130" data-mp="2jcktcnlrxkzs">
                <button type="button" onClick={() => toggleSort('loanAmount')} className="flex items-center gap-1 uppercase tracking-wider hover:text-slate-700 transition-colors" data-id="element-131" data-mp="xy6qg906pbld">
                  Loan Info <SortIcon column="loanAmount" data-id="element-132" data-mp="nu00l37r21n9" />
                </button>
              </th>
              <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-133" data-mp="a2bmwvhur546">
                Current Campaign
              </th>
              <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider" aria-sort={sortKey === 'lastContact' ? sortDir === 'asc' ? 'ascending' : 'descending' : 'none'} data-id="element-134" data-mp="ezbvpe636lyz">
                <button type="button" onClick={() => toggleSort('lastContact')} className="flex items-center gap-1 uppercase tracking-wider hover:text-slate-700 transition-colors" data-id="element-135" data-mp="3s6ww5kp2j4x7">
                  Last Contact <SortIcon column="lastContact" data-id="element-136" data-mp="1o1onfejqoalj" />
                </button>
              </th>
              <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-137" data-mp="1k031z4t52nzq">
                Next Follow-up
              </th>
              <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-138" data-mp="1f1rtoyihsljm">
                Status
              </th>
              <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right" data-id="element-139" data-mp="27pnykqi7tjrh">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-100" data-id="element-140" data-mp="22sx6t5x1szq8">
            {pagedLeads.length > 0 ? pagedLeads.map(lead => <LeadRow key={lead.id} lead={lead} onAction={handleAction} showOwner={showOwner} canAssignCampaign={canAssignCampaign} availableCampaigns={AVAILABLE_CAMPAIGNS} onAssignCampaign={assignCampaign} data-id="element-141" data-mp="1szq5px9cc5vy" />) : <tr data-id="element-142" data-mp="2xyv2ry5idf28">
                <td colSpan={showOwner ? 8 : 7} className="py-12 text-center text-slate-500" data-id="element-143" data-mp="2y0fy3pznd215">
                  No leads found matching the current filter.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      {totalRecords > 0 && <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-white" data-id="element-144" data-mp="3bxcvkaa9thr0">
          <p className="text-sm text-slate-500" data-id="element-145" data-mp="3gw637m2vmej9">
            Showing{' '}
            <span className="font-medium text-slate-700" data-id="element-146" data-mp="3lq0tx9gwftru">
              {startIndex + 1}–{Math.min(startIndex + PAGE_SIZE, totalRecords)}
            </span>{' '}
            of{' '}
            <span className="font-medium text-slate-700" data-id="element-147" data-mp="3voq044zfnps9">{totalRecords}</span>{' '}
            follow-ups
          </p>
          <div className="flex items-center gap-2" data-id="element-148" data-mp="3jvvg461u8kw">
            <Button variant="outline" size="sm" icon={<ChevronLeft size={16} data-id="element-150" data-mp="kt97mwqorgyv" />} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1} data-id="element-149" data-mp="ou1fbljwtdt1">
              Previous
            </Button>
            <span className="text-sm text-slate-600 px-2" data-id="element-151" data-mp="ttxcp1y3q6zm">
              Page {currentPage} of {totalPages}
            </span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} data-id="element-152" data-mp="654lfisfcnbc">
              Next
              <ChevronRight size={16} className="ml-2" data-id="element-153" data-mp="fvza0tt57yzy" />
            </Button>
          </div>
        </div>}

      <LeadActionModal lead={activeModal.lead} mode={activeModal.mode} open={activeModal.lead !== null} onClose={() => setActiveModal({
      lead: null,
      mode: 'call'
    })} onSubmit={handleModalSubmit} data-id="element-154" data-mp="b1cv4gnurob4" />
    </div>;
}
// A single checkbox row used inside the multi-select filter dropdown.
function CheckOption({
  label,
  checked,
  onChange
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return <label className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer select-none" data-id="element-155" data-mp="1p2hosi7iqa5g">
      <span className={`flex items-center justify-center h-4 w-4 rounded border transition-colors ${checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'}`} data-id="element-156" data-mp="2cs3nl4jzancx">
        {checked && <Check size={12} strokeWidth={3} data-id="element-157" data-mp="23qlzp00bxhdo" />}
      </span>
      <input type="checkbox" checked={checked} onChange={onChange} className="sr-only" data-id="element-158" data-mp="28qqg6q18mc3w" />
      <span className="text-sm text-slate-700" data-id="element-159" data-mp="sbywd1i5qd67">{label}</span>
    </label>;
}